import type { UserRole } from '../../types';

export type SystemCopilotDestination = {
  id: string;
  label: string;
  labelAr: string;
  pageId: string;
  href: string;
  roles: readonly UserRole[];
};

const ADMIN: readonly UserRole[] = ['admin'];
const ADMIN_AND_CALL_CENTER: readonly UserRole[] = ['admin', 'call_center'];

export const SYSTEM_COPILOT_DESTINATIONS: readonly SystemCopilotDestination[] = [
  { id: 'ops', label: 'Operations command', labelAr: 'مركز العمليات', pageId: 'ops', href: '/admin#ops', roles: ADMIN },
  { id: 'orders', label: 'Orders', labelAr: 'الطلبات', pageId: 'orders', href: '/admin#orders', roles: ADMIN },
  { id: 'kitchen', label: 'Kitchen', labelAr: 'المطبخ', pageId: 'kitchen', href: '/admin#kitchen', roles: ADMIN },
  { id: 'drivers', label: 'Drivers and delivery', labelAr: 'السائقون والتوصيل', pageId: 'drivers', href: '/admin#drivers', roles: ADMIN },
  { id: 'menu', label: 'Menu management', labelAr: 'إدارة القائمة', pageId: 'menu', href: '/admin#menu', roles: ADMIN },
  { id: 'inventory', label: 'Inventory', labelAr: 'المخزون', pageId: 'inventory', href: '/admin#inventory', roles: ADMIN },
  { id: 'categories', label: 'Categories', labelAr: 'الأقسام', pageId: 'menu', href: '/admin#categories', roles: ADMIN },
  { id: 'discounts', label: 'Discounts', labelAr: 'الخصومات', pageId: 'discounts', href: '/admin#discounts', roles: ADMIN },
  { id: 'delivery-fees', label: 'Delivery fees', labelAr: 'رسوم التوصيل', pageId: 'drivers', href: '/admin#delivery-fees', roles: ADMIN },
  { id: 'fee-adjustments', label: 'Delivery fee adjustments', labelAr: 'تعديلات رسوم التوصيل', pageId: 'drivers', href: '/admin#fee-adjustments', roles: ADMIN },
  { id: 'menu-wizard', label: 'Menu setup wizard', labelAr: 'معالج إعداد القائمة', pageId: 'menu', href: '/admin#menu-wizard', roles: ADMIN },
  { id: 'bulk-import', label: 'Bulk menu import', labelAr: 'استيراد القائمة', pageId: 'menu', href: '/admin#bulk-import', roles: ADMIN },
  { id: 'addons-removables-import', label: 'Modifier import', labelAr: 'استيراد الإضافات والاستبعادات', pageId: 'menu', href: '/admin#addons-removables-import', roles: ADMIN },
  { id: 'users', label: 'Staff management', labelAr: 'إدارة الموظفين', pageId: 'staff', href: '/admin#users', roles: ADMIN },
  { id: 'customers', label: 'Customers', labelAr: 'العملاء', pageId: 'customers', href: '/admin#customers', roles: ADMIN },
  { id: 'reviews', label: 'Reviews', labelAr: 'التقييمات', pageId: 'customers', href: '/admin#reviews', roles: ADMIN },
  { id: 'trust-scores', label: 'Customer trust scores', labelAr: 'مؤشرات موثوقية العملاء', pageId: 'customers', href: '/admin#trust-scores', roles: ADMIN },
  { id: 'printing', label: 'Printing', labelAr: 'الطباعة', pageId: 'printing', href: '/admin#printing', roles: ADMIN },
  { id: 'sales', label: 'Reports', labelAr: 'التقارير', pageId: 'reports', href: '/admin#sales', roles: ADMIN },
  { id: 'accounting', label: 'Accounting', labelAr: 'المحاسبة', pageId: 'reports', href: '/admin#sales', roles: ADMIN },
  { id: 'phone-setup', label: 'Phone setup', labelAr: 'إعداد الهاتف', pageId: 'phone_setup', href: '/admin#phone-setup', roles: ADMIN },
  { id: 'setup', label: 'Restaurant setup', labelAr: 'إعداد المطعم', pageId: 'setup', href: '/admin#setup', roles: ADMIN },
  { id: 'branding', label: 'Branding', labelAr: 'الهوية التجارية', pageId: 'setup', href: '/admin#branding', roles: ADMIN },
  { id: 'pos', label: 'Point of sale', labelAr: 'نقطة البيع', pageId: 'pos', href: '/pos', roles: ADMIN },
  { id: 'call-center', label: 'Call center', labelAr: 'مركز الاتصال', pageId: 'call_center', href: '/call-center', roles: ADMIN_AND_CALL_CENTER },
  { id: 'staff_workspace', label: 'Staff workspace', labelAr: 'مساحة الموظفين', pageId: 'ops', href: '/staff-workspace', roles: ['manager', 'accountant'] },
  { id: 'driver_home', label: 'Driver workspace', labelAr: 'مساحة السائق', pageId: 'drivers', href: '/driver', roles: ['driver'] },
];

export const systemCopilotCanOpen = (role: UserRole) => (
  ['admin', 'manager', 'accountant', 'call_center', 'driver', 'cashier', 'kitchen'].includes(role)
);

export const systemCopilotDestination = (destinationId: string, role: UserRole) => {
  const target = SYSTEM_COPILOT_DESTINATIONS.find((entry) => entry.id === destinationId && entry.roles.includes(role));
  if (!target) return null;
  // Accountants land on Reports inside the shared staff workspace.
  return target.id === 'staff_workspace' && role === 'accountant' ? { ...target, pageId: 'reports' } : target;
};

export const systemCopilotPageId = (pathname: string, hash: string, role: UserRole) => {
  if (pathname === '/pos') return 'pos';
  if (pathname === '/call-center') return 'call_center';
  if (pathname === '/driver') return 'drivers';
  if (pathname === '/staff-workspace') return role === 'accountant' ? 'reports' : 'ops';
  if (pathname === '/admin') {
    const section = hash.replace(/^#/, '');
    return SYSTEM_COPILOT_DESTINATIONS.find((target) => target.roles.includes(role) && target.href === `/admin#${section}`)?.pageId || section.replace(/-/g, '_') || 'ops';
  }
  return 'unknown';
};

export const systemCopilotLocationMatches = (target: SystemCopilotDestination, pathname: string, hash: string) => {
  const [targetPath, targetHash = ''] = target.href.split('#');
  return targetPath === pathname && targetHash === hash.replace(/^#/, '');
};
